import { z } from "zod";

/**
 * A Facebook account the fleet polls with, and the state of one captured
 * login taken from it.
 *
 * These two enums live here, next to `Role` and `PlanStatus`, for the same
 * reason those do: the database renders them as CHECK constraints (see
 * `enumCheck` in @snipe/db's schema), so a member added in this file is a
 * migration, and a member the code invents that this file does not list is
 * refused by Postgres rather than written.
 */

/**
 * What an identity is allowed to be used for right now.
 *
 *   active       Mint from it, poll with it.
 *   needs_login  It has no stored storage state, so no browser can be made
 *                logged in from it. Not damage and not a decision -- a row
 *                that has never had a human sit through Facebook's login.
 *   paused       Ours: we stopped using it (rotation, a quota we chose to keep
 *                back, an experiment). Reversible by us alone.
 *   blocked      Facebook's: a checkpoint, a disabled account. Reversible only
 *                by a human going through Facebook's own flow.
 *
 * `paused` and `blocked` are deliberately not one "inactive": the first is a
 * decision and the second is damage, and an operator looking at a dashboard
 * needs to know which of the two is in front of them before they touch it.
 *
 * `needs_login` is the third of those distinctions and the newest. Minting
 * now reads a storage state out of `identities.storage_state_encrypted`
 * rather than a Chromium profile directory on the minting host -- 1.4 KB of
 * cookies instead of a 221 MB directory -- so "this account has never been
 * logged in" became a state the database can hold and a dashboard can act
 * on, instead of a mint that failed somewhere on a worker with an obscure
 * message about a profile.
 */
export const IdentityStatus = z.enum(["active", "needs_login", "paused", "blocked"]);
export type IdentityStatus = z.infer<typeof IdentityStatus>;

/**
 * The lifecycle of one captured session.
 *
 *   live     Believed good, and inside its backstop expiry.
 *   burned   Taken out of rotation on purpose, with a reason. Never revived:
 *            a session that answered `field_exception` once answers it every
 *            time, and an earlier version of this system spent 24 requests
 *            proving that.
 *   expired  Its backstop ran out -- OUR clock, not Facebook's verdict. Not a
 *            fault, and kept apart from `burned` so that "how many sessions
 *            did we lose to Facebook this week" is a question the table can
 *            still answer. Worth remembering that this state is only as
 *            meaningful as the backstop: when that was three invented hours,
 *            `expired` mostly meant "we threw away a working login".
 */
export const FbSessionStatus = z.enum(["live", "burned", "expired"]);
export type FbSessionStatus = z.infer<typeof FbSessionStatus>;
